import React, { useCallback, useEffect, useState } from "react";
import AdminShell from "../../Components/admin/AdminShell";
import MediaUploadZone from "../../Components/admin/MediaUploadZone";
import {
  fetchMediaLibrary,
  MediaApiError,
  removeStoredMediaItem,
  uploadMediaFile,
} from "../../services/mediaApi";
import { getApiBase } from "../../services/apiConfig";

export default function MediaLibrary() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const list = await fetchMediaLibrary();
      setItems(list);
    } catch (e) {
      setError(e?.message || "Could not load media library.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpload = async (files) => {
    setError("");
    setUploading(true);
    try {
      for (let i = 0; i < files.length; i += 1) {
        await uploadMediaFile(files[i]);
      }
      await load();
    } catch (e) {
      const msg = e instanceof MediaApiError ? e.message : e?.message || "Upload failed.";
      setError(msg);
    } finally {
      setUploading(false);
    }
  };

  const copyUrl = async (id, url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      window.prompt("Copy this link:", url);
    }
  };

  const onRemove = (id) => {
    if (!window.confirm("Remove this image from your library list?")) return;
    removeStoredMediaItem(id);
    setItems((prev) => prev.filter((x) => x.id !== id));
  };

  const apiConfigured = Boolean(getApiBase());

  return (
    <AdminShell
      title="Media Library"
      subtitle="Upload images and copy their URLs for use in posts and events"
    >
      {!apiConfigured ? (
        <div className="mediaLibraryError" role="alert">
          Configure the API base URL to upload files.
        </div>
      ) : null}

      <MediaUploadZone onUpload={handleUpload} disabled={!apiConfigured} busy={uploading} />

      {error ? (
        <div className="mediaLibraryError" role="alert">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="adminCard" style={{ marginTop: 20, padding: 24 }}>
          <div className="adminSkeleton" style={{ width: "40%", height: 14 }} />
          <div className="adminSkeleton" style={{ width: "100%", height: 120, marginTop: 16, borderRadius: 12 }} />
        </div>
      ) : items.length === 0 ? (
        <div className="mediaLibraryEmpty" style={{ marginTop: 20 }}>
          No images yet. Upload above to get a hosted link you can copy.
        </div>
      ) : (
        <div className="mediaLibraryGrid" style={{ marginTop: 20 }}>
          {items.map((item) => (
            <div key={item.id} className="mediaLibraryCard">
              <div className="mediaLibraryThumbWrap">
                <img src={item.url} alt="" loading="lazy" />
              </div>
              <div className="mediaLibraryCardBody">
                {item.name ? <div className="mediaLibraryFileName">{item.name}</div> : null}
                <div className="mediaLibraryUrlRow">
                  <input type="text" readOnly className="mediaLibraryUrlInput" value={item.url} aria-label="Image URL" />
                </div>
                <div className="mediaLibraryCardActions">
                  <button
                    type="button"
                    className="mediaLibraryBtn mediaLibraryBtnPrimary"
                    onClick={() => copyUrl(item.id, item.url)}
                  >
                    {copiedId === item.id ? "Copied" : "Copy link"}
                  </button>
                  <button type="button" className="mediaLibraryBtn" onClick={() => window.open(item.url, "_blank", "noopener,noreferrer")}>
                    Open
                  </button>
                  <button type="button" className="mediaLibraryBtn mediaLibraryBtnDanger" onClick={() => onRemove(item.id)}>
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminShell>
  );
}
